import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import LabWelcomeWizard from "@/components/labs/LabWelcomeWizard";
import { MODULES, findModule, ACCENT } from "@/modules/registry";
import { ArrowLeft, ArrowRight, Clock, HelpCircle, Wrench } from "lucide-react";

// P5.4 — Lab keys that ship with a welcome wizard. Adding a new lab to
// this set + a `labWizard.labs.<key>` i18n block is the full surface for
// a new Lab's onboarding (no per-Lab page edits required).
const LAB_WIZARD_KEYS = new Set(["racket_lab"]);

// Feb 2026 (refined) — token → length-bucket map used to group Core AI
// capabilities into 3 visually balanced columns on the desktop layout.
// Buckets are tuned against the ES/EN reference; CJK and Arabic/Hebrew
// will naturally fit because their glyphs are shorter — the layout
// still flexes via the grid so a long word in any locale never breaks
// the card. Unknown tokens default to "short" so hidden roadmap modules
// with legacy `core: [...]` still render gracefully.
const CORE_CAP_LENGTH = {
  team_universe: "long",
  intelligent_management: "long",
  community_of_users: "long",
  auth: "long",
  marketplace: "medium",
  notifications: "medium",
  consent: "medium",
  calendar: "medium",
  profiles: "short",
  wishlist: "short",
  settings: "short",
  qr: "short",
};
const CORE_CAP_BUCKETS = ["long", "medium", "short"];

// Resolve `apps.coreCaps.<token>` with a Title-Cased fallback so the
// UI never leaks a raw snake_case path.
const resolveCapLabel = (capKey, t) => {
  const path = `apps.coreCaps.${capKey}`;
  const translated = t(path);
  return translated && translated !== path
    ? translated
    : capKey
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

// Single component handles the sublanding for visible coming-soon
// modules (Racket Lab today). The module is resolved from the URL
// slug (kebab-case) → registry lookup after converting back to
// snake_case.
//
// HUB-NO-PUBLIC-ROADMAP (Feb 2026, strategic decision):
// Modules marked `visible: false` are registered internally for
// roadmap tracking ONLY. They must NEVER expose strategy info via a
// direct URL (competitors / copycats may probe URLs). Both unknown
// slugs and `visible:false` slugs fall through to the generic
// "not found" block below — zero module name, zero tagline, zero
// status badge, zero core-capabilities leak.
const AppComingSoon = () => {
  const { t } = useLang();
  const { slug } = useParams();
  const { user } = useAuth();
  // /apps/racket-lab → "racket_lab"
  const key = (slug || "").replace(/-/g, "_");
  const m = findModule(key);

  // P5.4 — Wizard visibility logic. Open the wizard the FIRST time an
  // authenticated user lands on a Lab sublanding that has a wizard
  // configured. We persist the "already seen" state per user × lab on
  // the backend so it follows the user across devices. Anonymous /
  // logged-out visitors never see the wizard.
  const [wizardOpen, setWizardOpen] = useState(false);
  useEffect(() => {
    if (!user || !m || m.visible === false || !LAB_WIZARD_KEYS.has(key)) return;
    let cancelled = false;
    api.get(`/users/me/lab-onboarding/${key}`)
      .then((r) => {
        if (cancelled) return;
        if (!r.data?.state) setWizardOpen(true);
      })
      .catch(() => { /* fail-open: skip auto-open if endpoint hiccups */ });
    return () => { cancelled = true; };
  }, [user, m, key]);

  if (!m || m.visible === false) {
    return (
      <PublicLayout>
        <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
          <div className="text-center max-w-md" data-testid="apps-not-found">
            <h1 className="text-2xl font-semibold text-slate-900">{t("apps.notFoundTitle")}</h1>
            <p className="text-sm text-slate-600 mt-2">{t("apps.notFoundBody")}</p>
            <Link to="/apps" className="tsw-btn-back mt-4" data-testid="apps-back-to-hub">
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("apps.backToHub")}
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const Icon = m.icon;
  const accent = ACCENT[m.accentClass] || ACCENT.emerald;

  return (
    <PublicLayout>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16" data-testid={`apps-${m.key}-page`}>
          <Link to="/apps" className="tsw-btn-back mb-8" data-testid="apps-back-to-hub">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("apps.backToHub")}
          </Link>

          <div className={`rounded-2xl border ${accent.border} ${accent.bg} p-8 sm:p-10`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-14 h-14 rounded-2xl bg-white border ${accent.border} flex items-center justify-center ${accent.text}`}>
                <Icon className="w-6 h-6" />
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide font-medium text-slate-600 bg-white/80 border border-slate-200 px-2 py-1 rounded-full">
                <Clock className="w-3 h-3" /> {t("apps.statusComingSoon")}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              {t(`apps.${m.key}.title`)}
            </h1>
            <p className="text-base text-slate-700 mt-3 leading-relaxed">
              {t(`apps.${m.key}.tagline`)}
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-white/70 rounded-lg border border-white p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{t("apps.willSolve")}</div>
                <p className="text-slate-700 leading-relaxed">{t(`apps.${m.key}.willSolve`)}</p>
              </div>
              <div className="bg-white/70 rounded-lg border border-white p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{t("apps.coreReused")}</div>
                {/* Feb 2026 (refined v3) — switched from 3 columns to 2
                    balanced columns (6+6 for racket_lab's 12 caps).
                    Items are pre-ordered by length bucket (long →
                    medium → short) and then sliced at the midpoint so
                    each column has the same item count. `justify-
                    between` aligns col1 to the left edge and col2 to
                    the right; `flex-wrap` is a safety net for locales
                    where the two widest items exceed the card width
                    (DE / RU / HI) — col2 then drops below col1
                    instead of overlapping. `whitespace-nowrap` keeps
                    each capability on a single line whenever the
                    container allows it. Mobile uses the same block:
                    on narrow viewports flex-wrap naturally stacks
                    col2 under col1, achieving the 1-column
                    presentation the spec asks for. */}
                {(() => {
                  const ordered = CORE_CAP_BUCKETS.flatMap((b) =>
                    m.core.filter((k) => (CORE_CAP_LENGTH[k] || "short") === b),
                  );
                  const half = Math.ceil(ordered.length / 2);
                  const cols = [ordered.slice(0, half), ordered.slice(half)];
                  return (
                    <div
                      className="flex flex-wrap justify-between items-start gap-x-3 gap-y-1.5 text-[13px] text-slate-700"
                      data-testid={`apps-${m.key}-core-caps`}
                    >
                      {cols.map((items, idx) => (
                        <ul key={idx} className="space-y-1.5 min-w-0">
                          {items.map((capKey) => (
                            <li
                              key={capKey}
                              className="leading-snug whitespace-nowrap"
                              data-testid={`apps-cap-${capKey}`}
                            >
                              {resolveCapLabel(capKey, t)}
                            </li>
                          ))}
                        </ul>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/apps"
              className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
              data-testid="apps-cta-back"
            >
              <ArrowLeft className="w-3.5 h-3.5 rtl:rotate-180" /> {t("apps.backToHub")}
            </Link>
            <Link
              to="/app"
              className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="apps-cta-go-active"
            >
              {t("apps.goToActive")} <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
            </Link>
            {/* MF-1 (Feb 2026) — Admin/staff-only deep link into the operational
                Racket Lab module. The HUB ficha keeps `status: "coming_soon"`
                for the public, while admin/staff get a one-click bridge to
                `/app/racket-lab/tickets` so they don't have to remember the
                deep URL. Strictly gated to admin+staff; never rendered for
                regular users or anonymous visitors. Only shown on the
                `racket_lab` sublanding so the pattern doesn't leak into
                other coming-soon labs by accident. */}
            {user && m.key === "racket_lab" && (user.role === "admin" || user.role === "staff") && (
              <Link
                to="/app/racket-lab/tickets"
                className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="apps-cta-open-racket-lab-admin"
              >
                <Wrench className="w-3.5 h-3.5" /> {t("apps.racket_lab.openAdminCta")}
              </Link>
            )}
            {/* P5.4 — Re-open the Lab Welcome Wizard. Visible only for
                authenticated users on Labs that ship a wizard. Lets
                users revisit capabilities / data / benefits / permissions
                without depending on first-visit detection. */}
            {user && LAB_WIZARD_KEYS.has(m.key) && (
              <button
                type="button"
                onClick={() => setWizardOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                data-testid="apps-cta-wizard"
              >
                <HelpCircle className="w-3.5 h-3.5" /> {t("labWizard.steps.welcome")}
              </button>
            )}
          </div>

          <p className="text-xs text-slate-500 mt-8 italic" data-testid="apps-privacy-note">
            {t("apps.privacyNote")}
          </p>
        </div>
        {wizardOpen && (
          <LabWelcomeWizard
            labKey={m.key}
            onClose={() => setWizardOpen(false)}
          />
        )}
      </div>
    </PublicLayout>
  );
};

export default AppComingSoon;

// Helper exported for routing — list of slugs that should resolve to
// this single page. HUB-NO-PUBLIC-ROADMAP: only modules with
// `visible: true` AND `status === "coming_soon"` (today: Racket Lab)
// are surfaced. Hidden roadmap modules deliberately fall through to
// the NotFound block so direct URL probes return zero strategic
// information.
export const COMING_SOON_SLUGS = MODULES
  .filter((m) => m.status === "coming_soon" && m.visible === true)
  .map((m) => m.key.replace(/_/g, "-"));
